import React from 'react'
import { EntityDetail, EntityLocationDTO } from '@user/store/map/typings'
import { LocationDTO } from '@sales-center/common'
import Paper from '@mui/material/Paper/Paper'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { KnockStatusChip } from '@user/features/MapView/KnockStatusChip'
import { useData, useHandlers } from './hooks'
import { AccessTime } from '@workwave-tidal/core/icons/AccessTime'
import { formatKnockStatusCreatedInfo } from '@user/utils/knockStatusFormatting'
import { ViewKnockStatusHistoryButton } from '@user/features/MapView/MapBubbleContents/atoms/ViewKnockStatusHistoryButton'
import {
  ClearKnockStatusButton,
  ClearKnockStatusConfirmDialog,
  useKnockStatusEditPermission,
} from '@user/features/MapView/ClearKnockStatus'
import { EditableKnockStatusReason } from '@user/features/MapView/KnockStatusModal/EditableKnockStatusReason'

type Props = {
  entityDetails: EntityDetail | null
  variant: string
  serviceLocation: LocationDTO
  canUpdate: boolean
  onViewKnockStatusHistory?: (location: EntityLocationDTO) => void
  onRefresh?: () => void
}

export function KnockStatusBox(props: Props) {
  const {
    entityDetails,
    variant,
    serviceLocation,
    canUpdate,
    onViewKnockStatusHistory,
    onRefresh,
  } = props

  const {
    selectedServiceLocationId,
    isDraftLead,
    entityType,
    knockStatusLocationId,
    knockStatusHistoryId,
    knockStatus,
    hasActiveStatus,
    canViewHistory,
    knockStatusCreatedTime,
    knockStatusCreatedByName,
  } = useData({ entityDetails, variant, serviceLocation })

  const createdInfoText = formatKnockStatusCreatedInfo(
    knockStatusCreatedTime,
    knockStatusCreatedByName,
  )

  const [canEdit] = useKnockStatusEditPermission(entityType)

  const {
    clearConfirmOpen,
    isClearing,
    isSavingReason,
    openClearConfirm,
    closeClearConfirm,
    handleClearKnockStatus,
    handleSaveReason,
  } = useHandlers({
    entityDetails,
    isDraftLead,
    entityType,
    knockStatusLocationId,
    knockStatusHistoryId,
    selectedServiceLocationId,
    onRefresh,
  })

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 400,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
          }}
        >
          Knock status
        </Typography>
        {hasActiveStatus && canEdit && (
          <ClearKnockStatusButton onClick={openClearConfirm} />
        )}
      </Box>
      <Box>
        {knockStatus.status ? (
          <KnockStatusChip
            status={knockStatus.status}
            isDefault={knockStatus.isDefault}
          />
        ) : (
          <Typography variant="body2">-</Typography>
        )}
      </Box>

      <EditableKnockStatusReason
        reason={knockStatus.note}
        canEdit={canUpdate && hasActiveStatus && Boolean(knockStatusHistoryId)}
        isSaving={isSavingReason}
        onSave={handleSaveReason}
      />

      {createdInfoText && (
        <Typography
          variant="body2"
          sx={{
            mt: 0.6,
            color: '#0E283FB2',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <AccessTime fontSize="small" />
          {'  '}
          {createdInfoText}
        </Typography>
      )}

      {canViewHistory && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <ViewKnockStatusHistoryButton
            onClick={() => {
              if (onViewKnockStatusHistory && serviceLocation) {
                const location = {
                  latitude: serviceLocation.latitude ?? 0,
                  longitude: serviceLocation.longitude ?? 0,
                  serviceLocationId: serviceLocation.id,
                  entityId: entityDetails?.id ?? 0,
                  entityType: variant,
                  contactLocationId: serviceLocation.contactLocationId,
                } as EntityLocationDTO
                onViewKnockStatusHistory(location)
              }
            }}
          />
        </Box>
      )}

      <ClearKnockStatusConfirmDialog
        open={clearConfirmOpen}
        isClearing={isClearing}
        onCancel={closeClearConfirm}
        onConfirm={handleClearKnockStatus}
      />
    </Paper>
  )
}
